import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { SessionBranchInfo } from './SessionBranchInfo'
import { SessionPrInfo } from './SessionPrInfo'
import { SessionMetadataInfo } from './SessionMetadataInfo'
import { SessionTokenSummary } from './SessionTokenSummary'
import { WorkflowProgress } from '@/components/orchestration'
import { ListChecks } from 'lucide-react'
import type { SessionMetadata } from '@/lib/types'
import type { TokenUsage } from '@/types/chatbot'

export interface SessionContextSidebarProps {
  /** Session metadata to display */
  session: SessionMetadata
  /** Token usage data */
  tokenUsage?: TokenUsage | null
  /** Whether token usage is loading */
  isLoadingTokens?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Sticky sidebar displaying session context information.
 * Shows branch, PR, and session metadata when available.
 * Sections are conditionally rendered based on data availability.
 *
 * @example
 * ```tsx
 * <SessionContextSidebar
 *   session={sessionMetadata}
 *   className="w-72"
 * />
 * ```
 */
export function SessionContextSidebar({
  session,
  tokenUsage,
  isLoadingTokens,
  className
}: SessionContextSidebarProps) {
  const hasBranch = Boolean(session.workBranch)
  const hasPr = Boolean(session.prUrl)
  const hasTokens = (tokenUsage && tokenUsage.totalTokens > 0) || isLoadingTokens

  return (
    <Card
      data-testid="context-sidebar"
      className={cn('', className)}
    >
      <CardContent className="p-4 space-y-4">
        {/* Token Usage Section */}
        <SessionTokenSummary
          tokenUsage={tokenUsage}
          isLoading={isLoadingTokens}
        />

        {/* Divider after tokens if shown */}
        {hasTokens && <hr className="border-border" />}

        {/* Branch Section */}
        {hasBranch && (
          <SessionBranchInfo branch={session.workBranch} />
        )}

        {/* PR Section */}
        {hasPr && (
          <>
            {hasBranch && <hr className="border-border" />}
            <SessionPrInfo prUrl={session.prUrl} />
          </>
        )}

        {/* Session Info Section - Always shown */}
        {(hasBranch || hasPr) && <hr className="border-border" />}
        <SessionMetadataInfo
          createdAt={session.createdAt}
          messageCount={session.messageCount}
          createdByName={session.createdByName}
          createdByEmail={session.createdByEmail}
        />

        {/* Workflow Progress Section - Always shown */}
        <hr className="border-border" />
        <div className="space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <ListChecks className="h-4 w-4" />
            <span>Workflow</span>
          </h3>
          <WorkflowProgress
            status={session.status}
            checkpoint={session.checkpoint}
            errorMessage={session.errorMessage}
            startTime={session.createdAt}
            updatedAt={session.updatedAt}
          />
        </div>
      </CardContent>
    </Card>
  )
}
